import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Plus, Shield, Eye, Edit, Trash2, Key, RefreshCw,
  UserPlus, Crown, ShieldCheck, ShieldAlert, User, Clock,
  Activity, CheckCircle2, XCircle, AlertTriangle, Mail, Lock
} from 'lucide-react';
import { api, useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const ROLE_ICONS = {
  owner: Crown,
  admin: ShieldCheck,
  editor: Edit,
  viewer: Eye
};

const ROLE_COLORS = {
  owner: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
  admin: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
  editor: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30',
  viewer: 'text-gray-400 bg-gray-500/20 border-gray-500/30'
};

const ROLE_DESCRIPTIONS = {
  owner: 'Full access including billing and account management',
  admin: 'Full project access, can manage team (no billing)',
  editor: 'Can view and edit projects',
  viewer: 'Read-only access to projects'
};

const TeamMembersPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  
  const [newMember, setNewMember] = useState({
    email: '',
    name: '',
    password: '',
    role: 'editor'
  });

  const fetchData = useCallback(async () => {
    try {
      const [membersRes, permissionsRes] = await Promise.all([
        api.getTeamMembers(),
        api.getMyPermissions()
      ]);
      setMembers(membersRes.data || []);
      setPermissions(permissionsRes.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await api.getTeamActivity(50);
      setActivity(response.data || []);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'activity' && permissions?.permissions?.can_view_all_activity) {
      fetchActivity();
    }
  }, [activeTab, permissions, fetchActivity]);

  const handleAddMember = async () => {
    if (!newMember.email || !newMember.name || !newMember.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await api.addTeamMember(newMember);
      toast.success(`Team member ${newMember.name} added successfully`);
      setShowAddDialog(false);
      setNewMember({ email: '', name: '', password: '', role: 'editor' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;

    try {
      await api.updateTeamMember(selectedMember.id, {
        name: selectedMember.name,
        role: selectedMember.role,
        is_active: selectedMember.is_active
      });
      toast.success('Team member updated');
      setShowEditDialog(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!confirm(`Are you sure you want to remove ${member.name} from the team?`)) return;

    try {
      await api.removeTeamMember(member.id);
      toast.success(`${member.name} has been removed from the team`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleResetPassword = async (newPassword) => {
    if (!selectedMember || !newPassword) return;

    try {
      await api.resetMemberPassword(selectedMember.id, newPassword);
      toast.success(`Password reset for ${selectedMember.name}`);
      setShowPasswordDialog(false);
      setSelectedMember(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const canManageTeam = permissions?.permissions?.can_manage_team || permissions?.is_system_admin;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Team Management</h1>
              <p className="text-gray-400">Manage your team members and permissions</p>
            </div>
          </div>
        </div>

        {/* My Permissions Card */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-white font-medium">Your Role: <span className="capitalize">{permissions?.role}</span></p>
                  <p className="text-gray-400 text-sm">{ROLE_DESCRIPTIONS[permissions?.role] || 'Team member'}</p>
                </div>
              </div>
              {permissions?.is_system_admin && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">System Admin</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger value="members" className="data-[state=active]:bg-primary/20">
              <Users className="w-4 h-4 mr-2" /> Team Members ({members.length})
            </TabsTrigger>
            {permissions?.permissions?.can_view_all_activity && (
              <TabsTrigger value="activity" className="data-[state=active]:bg-primary/20">
                <Activity className="w-4 h-4 mr-2" /> Activity Log
              </TabsTrigger>
            )}
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            {/* Actions */}
            {canManageTeam && (
              <div className="flex justify-end mb-4">
                <Button onClick={() => setShowAddDialog(true)} data-tour="add-team-member">
                  <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
                </Button>
              </div>
            )}

            {/* Members Grid */}
            {members.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Team Members Yet</h3>
                  <p className="text-gray-400 mb-6">Add team members to collaborate on your projects.</p>
                  {canManageTeam && (
                    <Button onClick={() => setShowAddDialog(true)} data-tour="add-team-member">
                      <UserPlus className="w-4 h-4 mr-2" /> Add Your First Team Member
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="team-list">
                {members.map((member) => {
                  const RoleIcon = ROLE_ICONS[member.role] || User;
                  return (
                    <Card key={member.id} className="bg-white/5 border-white/10">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", ROLE_COLORS[member.role])}>
                              <RoleIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{member.name}</p>
                              <p className="text-gray-400 text-sm">{member.email}</p>
                            </div>
                          </div>
                          {!member.is_active && (
                            <Badge variant="outline" className="text-red-400 border-red-400">Inactive</Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mb-4">
                          <Badge className={ROLE_COLORS[member.role]}>{member.role}</Badge>
                          {member.last_login && (
                            <span className="text-gray-500 text-xs flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Last login: {new Date(member.last_login).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {canManageTeam && (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSelectedMember(member); setShowEditDialog(true); }}>
                              <Edit className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setSelectedMember(member); setShowPasswordDialog(true); }}>
                              <Key className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleRemoveMember(member)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Activity Log</CardTitle>
                <CardDescription>Recent team actions and changes</CardDescription>
              </CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-3">
                    {activity.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          log.action.includes('added') && "bg-green-500/20",
                          log.action.includes('removed') && "bg-red-500/20",
                          log.action.includes('updated') && "bg-blue-500/20",
                          log.action.includes('impersonation') && "bg-amber-500/20"
                        )}>
                          {log.action.includes('added') && <UserPlus className="w-4 h-4 text-green-400" />}
                          {log.action.includes('removed') && <XCircle className="w-4 h-4 text-red-400" />}
                          {log.action.includes('updated') && <Edit className="w-4 h-4 text-blue-400" />}
                          {log.action.includes('password') && <Key className="w-4 h-4 text-purple-400" />}
                          {log.action.includes('impersonation') && <ShieldAlert className="w-4 h-4 text-amber-400" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm">{log.details}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Member Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-card border-white/10 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Add Team Member</DialogTitle>
              <DialogDescription>Add a new member to your team</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input 
                  placeholder="John Doe" 
                  value={newMember.name}
                  onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input 
                  type="email"
                  placeholder="john@example.com" 
                  value={newMember.email}
                  onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input 
                  type="password"
                  placeholder="Set a password" 
                  value={newMember.password}
                  onChange={(e) => setNewMember({...newMember, password: e.target.value})}
                />
                <p className="text-xs text-gray-500">Minimum 6 characters</p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={newMember.role} onValueChange={(v) => setNewMember({...newMember, role: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin - Full project access, manage team</SelectItem>
                    <SelectItem value="editor">Editor - View and edit projects</SelectItem>
                    <SelectItem value="viewer">Viewer - Read-only access</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Security Note */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-400">
                    <strong>Security Note:</strong> Team members cannot access billing, delete the account, or remove the owner. Activity is logged for audit purposes.
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddMember}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Member Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-white/10 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Team Member</DialogTitle>
              <DialogDescription>Update member details and permissions</DialogDescription>
            </DialogHeader>
            {selectedMember && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input 
                    value={selectedMember.name}
                    onChange={(e) => setSelectedMember({...selectedMember, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input value={selectedMember.email} disabled className="opacity-50" />
                  <p className="text-xs text-gray-500">Email cannot be changed</p>
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={selectedMember.role} onValueChange={(v) => setSelectedMember({...selectedMember, role: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Account Status</p>
                    <p className="text-gray-400 text-sm">Enable or disable this account</p>
                  </div>
                  <Button 
                    variant={selectedMember.is_active ? "outline" : "default"}
                    size="sm"
                    onClick={() => setSelectedMember({...selectedMember, is_active: !selectedMember.is_active})}
                  >
                    {selectedMember.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateMember}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="bg-card border-white/10 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Reset Password</DialogTitle>
              <DialogDescription>Set a new password for {selectedMember?.name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input 
                  type="password"
                  id="new-password"
                  placeholder="Enter new password" 
                />
                <p className="text-xs text-gray-500">Minimum 6 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
              <Button onClick={() => {
                const newPass = document.getElementById('new-password').value;
                handleResetPassword(newPass);
              }}>
                <Key className="w-4 h-4 mr-2" /> Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TeamMembersPage;
